import { useContext, useLayoutEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import { getFirstChar } from "helpers/commonHelpers";
import { updateUserCustomSettings } from "api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useStateMachine } from "little-state-machine";

import Button from "components/atoms/Button/Button";
import testIds from "constants/testIds";
import NavContext from "components/molecules/Navigation/components/common/NavContext";
import { shipperAdminOptions, shipperSettingsOptions } from "../../../../shipper/Settings/shipperNavData";
import { providerAdminOptions, providerSettingsOptions } from "../../../../provider/Settings/providerNavData";

function SettingsNav({ permissions }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const settingsNavRef = useRef(null);

  const { navigationConfig, setNavigationConfig } = useContext(NavContext);
  const { company_name, first_name, last_name } = JSON.parse(permissions || "{}");
  const quotingSettings = permissions ? JSON.parse(permissions).permissions.quoting : {};
  const adminSettings = permissions ? JSON.parse(permissions).permissions.admin_settings : {};
  const offPlatformSettings = permissions ? JSON.parse(permissions).permissions.off_platform : {};
  const developerSettings = permissions ? JSON.parse(permissions).permissions.developer : {};
  const { state } = useStateMachine();
  const { user_type } = JSON.parse(permissions || "{}");
  const settingsOptions = user_type === "shipper" ? shipperSettingsOptions(testIds) : providerSettingsOptions(testIds);
  const adminOptions =
    user_type === "shipper"
      ? shipperAdminOptions(adminSettings, offPlatformSettings, intl, quotingSettings, developerSettings)
      : providerAdminOptions(adminSettings, intl, developerSettings);

  const currentSection = window.location.pathname.split("/").pop();

  useLayoutEffect(() => {
    const el = settingsNavRef.current;
    if (!el) {
      setNavigationConfig((prev) => ({ ...prev, settingNavWidth: 0 }));
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { offsetWidth } = entry.target;
      setNavigationConfig((prev) => ({ ...prev, settingNavWidth: offsetWidth }));
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [navigationConfig.expanded, navigationConfig.isMobile]);

  const handleSelectSection = (e) => {
    const key = e.currentTarget.getAttribute("keyname");
    navigate(`/settings/${key}`);
  };

  const handleNavigate = (url) => {
    window.open(url, "_blank");
  };

  const restartTour = () => {
    updateUserCustomSettings({ tourState: {} });
    const customSettings = localStorage.getItem("customSettings");
    // remove tourState key from customSettings
    const customSettingsObj = JSON.parse(customSettings);
    delete customSettingsObj.tourState;
    localStorage.setItem("customSettings", JSON.stringify(customSettingsObj));
    toast("Tour restarted", { type: "success" });
  };

  if (navigationConfig.isMobile && !navigationConfig.expanded) return;

  return (
    <div
      ref={settingsNavRef}
      style={{
        zIndex: navigationConfig.isMobile && navigationConfig.expanded && 50,
      }}
      className={`settings-nav ${navigationConfig.expanded && "settings-nav-expanded"}`}
    >
      <div className="d-flex align-items-center" style={{ marginBottom: "16px" }}>
        <div className="avatar-container">
          {getFirstChar(first_name)}
          {getFirstChar(last_name)}
        </div>
        <div>
          <h5>
            {first_name} {getFirstChar(last_name)}.
          </h5>
          <div className="text-secondary font-b3" style={{ marginTop: "6.5px" }}>
            {state.companyName || company_name}
          </div>
        </div>
      </div>

      <div className="d-flex flex-column">
        {settingsOptions.map((button) => (
          <Button
            key={button.keyName}
            className={`rounded-pill settings-nav-button no-wrap ${currentSection === button.keyName && "settings-active"}`}
            keyname={button.keyName}
            data-testid={button.testId}
            onClick={(e) => handleSelectSection(e)}
          >
            <FormattedMessage id={button.message} defaultMessage={button.defaultText} />
          </Button>
        ))}
      </div>

      {adminOptions.length > 0 && (
        <div className="d-flex flex-column" style={{ marginTop: "62px" }}>
          <h6 style={{ paddingLeft: "16px", marginBottom: "14px" }}>
            <FormattedMessage id="settings.adminSettings" defaultMessage="Admin settings" />
          </h6>
          {adminOptions.map((option) => {
            if (option.permission) {
              return (
                <Button
                  key={option.key}
                  className={`rounded-pill settings-nav-button ${currentSection === option.key && "settings-active"}`}
                  keyname={option.key}
                  onClick={(e) => handleSelectSection(e)}
                >
                  {option.label}
                </Button>
              );
            }
            return null;
          })}
        </div>
      )}

      <div className="d-flex flex-column" style={{ marginTop: "62px" }}>
        <h6 style={{ paddingLeft: "16px", marginBottom: "14px" }}>
          <FormattedMessage id="settings.platformSettings" defaultMessage="Platform settings" />
        </h6>
        <Button onClick={restartTour} className="rounded-pill settings-nav-button">
          <FormattedMessage id="settings.side-menu.restartTour" defaultMessage="Restart Tour" />
        </Button>

        <Button className="rounded-pill settings-nav-button" onClick={() => handleNavigate("https://help.cargobase.com")}>
          <FormattedMessage id="settings.side-menu.helpCenter" defaultMessage="Help center" />
        </Button>

        <Button className="rounded-pill settings-nav-button" onClick={() => navigate("/release-notes")}>
          <FormattedMessage id="releaseNotes.releaseNotes" defaultMessage="Release notes" />
        </Button>

        <Button className="rounded-pill settings-nav-button" onClick={() => handleNavigate("https://status.cargobase.com")}>
          <FormattedMessage id="settings.side-menu.platformStatus" defaultMessage="Platform status" />
        </Button>

        <Button
          className="rounded-pill settings-nav-button"
          onClick={() => handleNavigate("https://vimeo.com/cargobase/albums")}
        >
          <FormattedMessage id="settings.side-menu.trainingVideos" defaultMessage="Training videos" />
        </Button>
      </div>
    </div>
  );
}

SettingsNav.propTypes = {
  permissions: PropTypes.string.isRequired,
};

export default SettingsNav;
